import React from 'react';
import { Loader, ProviderLayout, ProviderLayoutPageHeader } from '@uplift/ui-v2';
import { Avatar, Box, Container, List, ListItemAvatar, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useAllCollaborativeChartsReviewsQuery, useCollaboratePhysicianMatchByIdForPhysicianQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { DescriptionOutlined } from '@mui/icons-material';
import { CalendarDateService } from '@uplift/services';
import { DateFormats, providerChartsReviewRoute } from '@uplift/constants';
import { useHistory } from 'react-router-dom';
import { CollaborativeChartsReview } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';


const ChartsReviewListItem: React.VFC<{ chartsReview: CollaborativeChartsReview }> = ({ chartsReview }) => {
  const me = useCurrentuser();
  const history = useHistory();
  const { data: match } = useCollaboratePhysicianMatchByIdForPhysicianQuery(me.id, chartsReview.matchId);
  const { data: providerName } = useUserDisplayNameQuery(match.providerId, { suspense: true });
  const expireAt = CalendarDateService.date(chartsReview.expireAt, DateFormats.ISO_DATE);
  const expired = expireAt.isBefore(CalendarDateService.date());

  return (
    <ListItemButton
      sx={{ bgcolor: '#fff' }}
      onClick={() => history.push(providerChartsReviewRoute.createLink({ reviewId: chartsReview.id }))}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'primary.background', height: '64px', width: '64px' }}><DescriptionOutlined color="secondary" /></Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{ ml: 2 }}
        primary={`${CalendarDateService.date(chartsReview.period.start, DateFormats.ISO_DATE).format('MMMM D')} for ${providerName.full}`}
        secondary={`${expired ? 'Overdue' : 'Due'} ${expireAt.fromNow()}`}
      />
    </ListItemButton>
  );
};

const SubmittedChartsReviewListItem: React.VFC<{ chartsReview: CollaborativeChartsReview }> = ({ chartsReview }) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const history = useHistory();
  const { data: match } = useCollaboratePhysicianMatchByIdForPhysicianQuery(me.id, chartsReview.matchId);
  const { data: providerName } = useUserDisplayNameQuery(match.providerId, { suspense: true });
  const submittedAt = CalendarDateService.date(chartsReview.submittedAt).tz(timezone);

  return (
    <ListItemButton
      sx={{ bgcolor: '#fff' }}
      onClick={() => history.push(providerChartsReviewRoute.createLink({ reviewId: chartsReview.id }))}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'primary.background', height: '64px', width: '64px' }}><DescriptionOutlined color="secondary" /></Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{ ml: 2 }}
        primary={`${CalendarDateService.format(CalendarDateService.date(chartsReview.period.start, DateFormats.ISO_DATE), 'MMMM D', 'MMMM D, YYYY')} for ${providerName.full}`}
        secondary={`Submitted at ${submittedAt.format(DateFormats.DATETIME)}`}
      />
    </ListItemButton>
  );
};

const ChartsReviewsPageContent: React.VFC = () => {
  const { data: pendingChartsReviews } = useAllCollaborativeChartsReviewsQuery({ submitted: false });
  const { data: completedChartsReviews } = useAllCollaborativeChartsReviewsQuery({ submitted: true });

  if (!pendingChartsReviews?.length && !completedChartsReviews?.length) {
    return (
      <Typography variant="h3" color="textDisabled" sx={{ height: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Nothing found</Typography>
    );
  }

  return (
    <Box>
      <Container sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {pendingChartsReviews?.length ? (
          <Box>
            <Typography variant="h4">Pending Review</Typography>
            <List>
              {pendingChartsReviews.map(cr => (
                <ChartsReviewListItem
                  key={cr.id}
                  chartsReview={cr}
                />
              ))}
            </List>
          </Box>
        ) : null}

        {completedChartsReviews?.length ? (
          <Box>
            <Typography variant="h4">Completed</Typography>
            <List>
              {completedChartsReviews.map(cr => (
                <SubmittedChartsReviewListItem
                  key={cr.id}
                  chartsReview={cr}
                />
              ))}
            </List>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};


export const ChartsReviewsPage: React.VFC = () => {
  return (
    <ProviderLayout>
      <ProviderLayoutPageHeader title="Charts Reviews" divider />
      <React.Suspense fallback={<Loader color="primary" height="400px" />}>
        <ChartsReviewsPageContent />
      </React.Suspense>
    </ProviderLayout>
  );
};
